/**
 * The new / edit alert form: a name, the people, the morning send time, on or
 * off, and the problems to include — ticked one by one under Critical, Warning
 * and Cleanup headings, plus an Expected heading for the green groups the backend
 * offers (the certified cars shown correctly, Ian 2026-10-14). Cleanup is opt-in and
 * unticked by default (Ian, 2026-10-07); INVI's own
 * problems are never offered, and the note at the bottom says so. Field styles copy
 * pages/hq/ScheduledReportsPage.tsx (inputStyle ~235, labelStyle ~248).
 */
import React, { useState } from "react"

import { AuditRooftopOption } from "../../../api/audit"
import { AuditAlert, AuditAlertInput, AuditAlertProblem, AuditAlertProblemOption } from "../../../api/auditAlerts"
import { THEME } from "../../../config/theme"
import { useIsMobile } from "../../../hooks/useIsMobile"
import SeverityDot, { severityWord } from "../worklist/SeverityDot"
import { fromTimeInput, parseRecipients, toTimeInput } from "./alertFormatting"

interface Props {
  options: AuditAlertProblemOption[]
  /** The group's stores, the same list the audit page's store tabs use. */
  stores: AuditRooftopOption[]
  /** The alert being edited, or null for a new one. */
  initial: AuditAlert | null
  saving: boolean
  error: string | null
  onSave: (input: AuditAlertInput) => void
  onCancel: () => void
}

/** Ian's default (2026-10-06): every morning at 7:00 Pacific. */
const DEFAULT_TIME = "07:00"
const SEVERITY_ORDER = ["critical", "warning", "cleanup", "expected"]
const SEVERITY_CAPTIONS: Record<string, string> = {
  critical: "costing sales now",
  warning: "on a clock, not critical yet",
  cleanup: "hygiene, no sales impact — only if someone wants the list",
  // Only green groups the backend marks as offered appear (today: the certified
  // cars shown correctly). They ride in the email but are never counted as needing attention.
  expected: "nothing to fix — the cars that are right, for whoever tracks them",
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "40px",
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: "9px 12px",
  fontSize: "14px",
  fontFamily: THEME.fonts.primary,
  color: THEME.colors.neutral.light.textPrimary,
  boxSizing: "border-box",
  outline: "none",
}

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "13px",
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: "4px",
}

const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 6,
}

// The section eyebrow the worklist stands above each severity's groups
// (AuditWorklistView.tsx SeverityHeading).
const sectionStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: THEME.spacing.sm,
  margin: `${THEME.spacing.md} 0 ${THEME.spacing.xs}`,
  fontSize: THEME.typography.caption.size,
  fontWeight: THEME.typography.caption.weight,
  letterSpacing: THEME.typography.caption.tracking,
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

// Brand primary button — components/audit/DownloadMenu.tsx:146-163 (buttonStyle).
const primaryButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  borderRadius: THEME.borderRadius.md,
  border: "none",
  backgroundColor: THEME.colors.navy,
  color: THEME.colors.white,
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: THEME.fonts.primary,
})

const secondaryButtonStyle: React.CSSProperties = {
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 14,
  fontWeight: 500,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.white,
  color: THEME.colors.neutral.light.textPrimary,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}

/** The select's value for "the whole group" — an empty slug. */
const WHOLE_GROUP = ""

export default function AuditAlertForm({ options, stores, initial, saving, error, onSave, onCancel }: Props) {
  const isMobile = useIsMobile()
  const [name, setName] = useState(initial?.name ?? "")
  const [storeSlug, setStoreSlug] = useState(initial?.rooftop_slug ?? WHOLE_GROUP)
  const [recipientsText, setRecipientsText] = useState(initial?.recipients.join(", ") ?? "")
  const [time, setTime] = useState(initial ? toTimeInput(initial.hour, initial.minute) : DEFAULT_TIME)
  const [enabled, setEnabled] = useState(initial?.is_enabled ?? true)
  const [ticked, setTicked] = useState<Set<string>>(
    () => new Set((initial?.problems ?? []).map((problem) => problem.slug)),
  )
  const [localError, setLocalError] = useState<string | null>(null)

  const toggle = (option: AuditAlertProblemOption) => {
    const key = option.slug
    setTicked((current) => {
      const next = new Set(current)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  const submit = () => {
    setLocalError(null)
    const recipients = parseRecipients(recipientsText)
    if (!name.trim()) {
      setLocalError("Give the alert a name")
      return
    }
    if (recipients.length === 0) {
      setLocalError("Add at least one recipient email")
      return
    }
    const problems: AuditAlertProblem[] = options
      .filter((option) => ticked.has(option.slug))
      .map((option) => ({ slug: option.slug }))
    if (problems.length === 0) {
      setLocalError("Tick at least one problem")
      return
    }
    const sendTime = fromTimeInput(time)
    if (sendTime === null) {
      setLocalError("Pick a send time")
      return
    }
    onSave({
      name: name.trim(), recipients, problems, ...sendTime, is_enabled: enabled,
      rooftop_slug: storeSlug || null,
    })
  }

  // One row per problem. Options arrive critical-first from the server.
  const renderOption = (option: AuditAlertProblemOption) => (
    <label
      key={option.slug}
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "4px 0",
        fontSize: 13.5,
        cursor: "pointer",
        color: THEME.colors.neutral.light.textPrimary,
      }}
    >
      <input
        type="checkbox"
        checked={ticked.has(option.slug)}
        onChange={() => toggle(option)}
        style={{ marginTop: 3 }}
      />
      <span>
        {option.label}
        <span style={{ display: "block", fontSize: 12, color: THEME.colors.neutral.light.textSecondary }}>
          {option.action}
        </span>
      </span>
    </label>
  )

  const renderSeverity = (severity: string) => (
    <React.Fragment key={severity}>
      <div style={sectionStyle}>
        <SeverityDot severity={severity} decorative />
        {severityWord(severity)}
        <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>
          · {SEVERITY_CAPTIONS[severity]}
        </span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", columnGap: 28 }}>
        {options.filter((option) => option.severity === severity).map(renderOption)}
      </div>
    </React.Fragment>
  )

  const shownError = localError ?? error

  return (
    <div style={{ padding: THEME.spacing.md }}>
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: `${THEME.spacing.md} 28px` }}>
        <div>
          <label style={labelStyle} htmlFor="alert-name">Alert name</label>
          <input id="alert-name" style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="Website" />
          <div style={hintStyle}>Named in the email&rsquo;s footer, so recipients know which alert sent it.</div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="alert-recipients">Recipients</label>
          <textarea
            id="alert-recipients"
            style={{ ...inputStyle, minHeight: 64, resize: "vertical" }}
            value={recipientsText}
            onChange={(e) => setRecipientsText(e.target.value)}
            placeholder="email@example.com, another@example.com"
          />
          <div style={hintStyle}>Any email address, separated by commas. They do not need an INVI login.</div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="alert-time">Sends every morning at (Pacific)</label>
          <input id="alert-time" type="time" style={{ ...inputStyle, maxWidth: 220 }} value={time} onChange={(e) => setTime(e.target.value)} />
          <div style={hintStyle}>
            One email per person per day. Nothing goes out if the audit has not run since the last email.
          </div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="alert-store">Store</label>
          <select
            id="alert-store"
            style={{ ...inputStyle, maxWidth: 280 }}
            value={storeSlug}
            onChange={(e) => setStoreSlug(e.target.value)}
          >
            <option value={WHOLE_GROUP}>All stores (the whole group)</option>
            {stores.map((store) => (
              <option key={store.slug} value={store.slug}>{store.audit_label}</option>
            ))}
          </select>
          <div style={hintStyle}>
            Pick a store and the email carries only that store&rsquo;s cars, with the store named up front.
          </div>
        </div>
        <div>
          <span style={labelStyle}>Status</span>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer", marginTop: 8 }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Enabled
          </label>
        </div>
      </div>

      <div style={{ ...labelStyle, marginTop: THEME.spacing.md }}>Problems to include</div>
      {SEVERITY_ORDER.filter((severity) => options.some((option) => option.severity === severity)).map(renderSeverity)}
      <div
        style={{
          marginTop: THEME.spacing.md,
          border: `1px dashed ${THEME.colors.neutral.light.border}`,
          borderRadius: THEME.borderRadius.md,
          padding: "10px 14px",
          fontSize: 12.5,
          color: THEME.colors.neutral.light.textSecondary,
          background: THEME.colors.neutral.light.background,
        }}
      >
        <strong style={{ color: THEME.colors.neutral.light.textPrimary }}>Cleanup and green ride last.</strong>{" "}
        Ticked cleanup problems go in the same morning email, in their own block after the red and amber
        ones; a ticked green group comes last of all, listed but never counted as needing attention.
        INVI&apos;s own problems, such as a status word it cannot translate, go to INVI, not to you.
      </div>

      {shownError && (
        <div style={{ marginTop: THEME.spacing.sm, fontSize: 13, color: THEME.colors.semantic.danger }}>{shownError}</div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: THEME.spacing.md }}>
        <button type="button" style={secondaryButtonStyle} onClick={onCancel}>Cancel</button>
        <button type="button" style={primaryButtonStyle(saving)} disabled={saving} onClick={submit}>
          {saving ? "Saving…" : initial ? "Save changes" : "Save alert"}
        </button>
      </div>
    </div>
  )
}
